import React, {useEffect, useState} from 'react';
import {motion} from 'framer-motion';
import {FiCheck} from 'react-icons/fi';
import {useWorkshopSchedule} from '../hooks/useWorkshopSchedule';

// Special offer countdown - 40 minutes from page load
const OFFER_DURATION = 40 * 60 * 1000;

function useOfferCountdown() {
  const [timeLeft, setTimeLeft] = useState(() => {
    const savedEndTime = localStorage.getItem('offerEndTime');
    if (savedEndTime) {
      const endTime = parseInt(savedEndTime);
      const remaining = Math.max(0, endTime - Date.now());
      if (remaining > 0) {
        return getOfferTimeParts(remaining);
      }
    }
    const endTime = Date.now() + OFFER_DURATION;
    localStorage.setItem('offerEndTime', endTime.toString());
    return getOfferTimeParts(OFFER_DURATION);
  });

  useEffect(() => {
    const timer = setInterval(() => {
      const savedEndTime = localStorage.getItem('offerEndTime');
      if (savedEndTime) {
        const endTime = parseInt(savedEndTime);
        const remaining = Math.max(0, endTime - Date.now());
        if (remaining === 0) {
          const newEndTime = Date.now() + OFFER_DURATION;
          localStorage.setItem('offerEndTime', newEndTime.toString());
          setTimeLeft(getOfferTimeParts(OFFER_DURATION));
        } else {
          setTimeLeft(getOfferTimeParts(remaining));
        }
      }
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return timeLeft;
}

function getOfferTimeParts(milliseconds) {
  const seconds = Math.floor((milliseconds / 1000) % 60);
  const minutes = Math.floor((milliseconds / 1000 / 60) % 60);
  const hours = Math.floor((milliseconds / (1000 * 60 * 60)) % 24);
  const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));
  return {days, hours, minutes, seconds};
}

export default function HeroSection({openRegistration = () => {}}) {
  const {workshopDetails} = useWorkshopSchedule();
  const offerTimeLeft = useOfferCountdown();

  const handleRegisterClick = () => {
    openRegistration();
  };

  return (
    <header className="hero-container">
      {/* Top thin bar */}
      <div className="hero-top-bar">
        💫 Women Are Building Meaningful Life Coach Careers — Enakkum Identity Irukkanum
      </div>

      <section className="hero-section">
        <div className="hero-inner">
          <motion.div
            initial={{opacity: 0, y: -30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.7}}
            className="hero-headline-area"
          >
            <motion.h1
              className="hero-title"
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.7, delay: 0.3}}
            >
              <span className="highlight">
                You're More Than Just a Homemaker or Employee
              </span>
              <br />
              <span className="subtitle-text">
                You take care of everyone. Family. Work. Responsibilities. But deep inside, you feel: "Enakkum enna oru identity irukkanum." Your listening skills, life experience, and emotional strength are exactly what great life coaches are made of.
              </span>
            </motion.h1>

            <motion.p
              style={{
                fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                color: '#be185d',
                marginBottom: '2rem',
                lineHeight: '1.6',
                textAlign: 'center',
                fontWeight: '600',
              }}
              initial={{opacity: 0, y: 20}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.5}}
            >
              In this free workshop, I'll show you how women are turning their life experience into a meaningful coaching career — with flexibility, confidence, and income. Idhu job illa. Idhu purpose + income.
            </motion.p>
          </motion.div>

          <div className="hero-columns">
            {/* VIDEO SECTION */}
            <motion.div
              initial={{opacity: 0, x: -40}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.7, delay: 0.2}}
              className="hero-left-column"
            >
              <motion.div
                className="hero-image-card"
                whileHover={{scale: 1.02}}
                style={{
                  overflow: 'hidden',
                  aspectRatio: '1 / 1',
                  backgroundColor: '#000',
                }}
              >
                <video
                  src="https://storage.googleapis.com/msgsndr/Te8QGpu12zbMxZIVQlZi/media/696c57d1b34b6424527920ae.mp4"
                  poster="https://storage.files-vault.com/uploads/1760945103-H0PweDnHcx.webp"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  style={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              </motion.div>
            </motion.div>

            {/* RIGHT: Offer Panel */}
            <motion.div
              initial={{opacity: 0, x: 40}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.7, delay: 0.3}}
              className="hero-right-column"
            >
              <div className="offer-panel shine-animation">
                <motion.div
                  className="offer-header"
                  initial={{opacity: 0, scale: 0.9}}
                  animate={{opacity: 1, scale: 1}}
                  transition={{duration: 0.5, delay: 0.6}}
                >
                  <FiCheck className="offer-check" />
                  <span>
                    Join the LIVE 3-Hour Workshop &<br />
                    Start Your Life Coach Journey! 🆓
                  </span>
                </motion.div>

                <div className="offer-expiry">
                  <div className="expiry-text">
                    Limited Free Seats for Women — Register Now!
                  </div>

                  <motion.div
                    className="countdown-display"
                    initial={{opacity: 0, y: 20}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.6, delay: 0.8}}
                  >
                    {[
                      {label: 'Days', value: offerTimeLeft.days},
                      {label: 'Hours', value: offerTimeLeft.hours},
                      {label: 'Minutes', value: offerTimeLeft.minutes},
                      {label: 'Seconds', value: offerTimeLeft.seconds, pulse: true},
                    ].map((unit, idx) => (
                      <div className="countdown-unit" key={idx}>
                        <motion.div
                          className={`countdown-number ${unit.pulse ? 'pulse-animation' : ''}`}
                          key={unit.value}
                          initial={{scale: 1.2}}
                          animate={{scale: 1}}
                          transition={{duration: 0.3}}
                        >
                          {String(unit.value || 0).padStart(2, '0')}
                        </motion.div>
                        <div className="countdown-label">{unit.label}</div>
                      </div>
                    ))}
                  </motion.div>
                </div>

                <motion.div
                  className="offer-time"
                  initial={{opacity: 0}}
                  animate={{opacity: 1}}
                  transition={{duration: 0.5, delay: 1}}
                >
                  Next Batch: {workshopDetails.formattedDate} —{' '}
                  {workshopDetails.timeString}
                </motion.div>

                <motion.button
                  onClick={handleRegisterClick}
                  className="offer-cta"
                  aria-haspopup="dialog"
                  aria-label="Register for the workshop"
                  initial={{opacity: 0, y: 20}}
                  animate={{opacity: 1, y: 0}}
                  transition={{duration: 0.6, delay: 1.2}}
                  whileHover={{scale: 1.05, y: -2}}
                  whileTap={{scale: 0.98}}
                >
                  YES! I WANT MY IDENTITY NOW 💫
                </motion.button>

                <motion.div
                  className="offer-bonus"
                  initial={{opacity: 0}}
                  animate={{opacity: 1}}
                  transition={{duration: 0.5, delay: 1.4}}
                >
                  <div className="bonus-text">
                    <span className="flame-icon">🔥</span>
                    Only 50 FREE Seats for Women Who Want Purpose + Respect!
                  </div>
                  <div className="progress-container">
                    <div className="progress-bar">
                      <motion.div
                        className="progress-fill"
                        initial={{width: '0%'}}
                        animate={{width: '75%'}}
                        transition={{duration: 2, delay: 1.6}}
                      ></motion.div>
                    </div>
                    <div className="progress-text">
                      75% Seats Booked • Flexible • Family-Friendly • Empowering
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </header>
  );
}